import { canUseDOM } from '@ps/web-core-utils';
import type { CasinoPlayerGames } from '@ps/web-martech-cms-types';
import { trim } from 'lodash';
import { takeUntil } from 'rxjs/operators';

import { getIcon, isFSB, preload, setLoading, transformParams, userUpdates } from '@swf/casino';
import type { UseWidget } from '@swf/core/hooks/useWidget';
import { useWidget } from '@swf/core/hooks/useWidget';
import { areGamesVisibleByConfig, initialState } from '@swf/core/models/casino/utils';
import type {
  ExtractDispatchFromModel,
  ExtractSelectFromModel,
  RootState,
  SelectorCreator,
  Slicer,
  WidgetEffectParams,
  WidgetModelConfig
} from '@swf/core/store';
import type { CasinoGame, StreamData } from '@swf/core/types/CasinoGame';
import { currencyChange, logout } from '@swf/core/utils/actions';
import { gamesPerColumn } from '@swf/core/utils/casino';

export interface CasinoPlayerGamesWidgetState extends StreamData<CasinoGame> {
  loading: boolean;
  title?: string;
}

const reducers = {
  set: (state: CasinoPlayerGamesWidgetState, payload: CasinoPlayerGamesWidgetState) => ({
    ...state,
    ...payload
  }),
  setLoading
};

const effects = {
  preload,
  userUpdates,
  async userLoggedIn(payload: undefined, params: WidgetEffectParams<CasinoPlayerGames>) {
    const {
      dispatch,
      widget: { uid }
    } = params;
    const widgetDispatch: ExtractDispatchFromModel<CasinoPlayerGamesWidgetModel> = dispatch[uid];
    await widgetDispatch.fetch();
  },
  async userLoggedOut(payload: undefined, params: WidgetEffectParams<CasinoPlayerGames>) {
    const {
      dispatch,
      widget: { uid }
    } = params;
    const widgetDispatch: ExtractDispatchFromModel<CasinoPlayerGamesWidgetModel> = dispatch[uid];
    await widgetDispatch.fetch();
  },
  async fetch(payload: undefined, params: WidgetEffectParams<CasinoPlayerGames>) {
    const {
      actions,
      dispatch,
      select,
      request,
      requestStream,
      rootState,
      widget: { uid, game_service: service, params: body }
    } = params;
    const widgetDispatch: ExtractDispatchFromModel<CasinoPlayerGamesWidgetModel> = dispatch[uid];
    const widgetSelect: ExtractSelectFromModel<CasinoPlayerGamesWidgetModel> = select[uid];
    const options = { service, body: transformParams(body) };
    const onLogout = logout(actions) as NonNullable<ReturnType<typeof logout>>;

    if (
      !areGamesVisibleByConfig({
        hasState: widgetSelect.get(rootState)?.games?.length > 0,
        params,
        service
      })
    ) {
      return;
    }

    if (!canUseDOM) {
      const { body: data } = await request<CasinoPlayerGamesWidgetState>(options);
      widgetDispatch.set({ ...data, loading: false });
    } else {
      requestStream<CasinoPlayerGamesWidgetState>(options, undefined)
        .pipe(takeUntil(currencyChange(actions) as typeof actions), takeUntil(onLogout))
        .subscribe((update) => widgetDispatch.set({ ...update, loading: false }));
    }
  }
};

const selectors = (
  slice: Slicer<CasinoPlayerGamesWidgetState>,
  createSelector: SelectorCreator
) => ({
  get() {
    return slice;
  },
  getTitle() {
    return createSelector(
      slice,
      (state: RootState, config: CasinoPlayerGames) => config,
      ({ title }, config) => trim(config?.display_title) || title || ''
    );
  },
  getGames() {
    return createSelector(
      slice,
      (rootState: RootState, config: CasinoPlayerGames) => config.mosaic_layout,
      ({ games }, mosaicLayout) => {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const { enabled, section_1, section_2, section_3 } = mosaicLayout;
        if (enabled) {
          const sections = [section_1, section_2, section_3];
          const packetSizes = sections.map((section) => gamesPerColumn(section));
          const clone = [...games];
          const result = [];
          for (let i = 0; clone.length; i++) {
            const neededSize = packetSizes[i % packetSizes.length];
            const segment = clone.splice(0, neededSize);
            if (segment.length >= neededSize) {
              result.push(...segment);
            }
          }
          return result;
        } else {
          return games;
        }
      }
    );
  },
  getIcon,
  isFSB
});

export interface CasinoPlayerGamesWidgetModel
  extends WidgetModelConfig<CasinoPlayerGames, CasinoPlayerGamesWidgetState> {
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

export const playerGamesWidgetModel: CasinoPlayerGamesWidgetModel = {
  state: initialState,
  reducers,
  effects,
  selectors
};

export const usePlayerGamesWidgetModel: UseWidget<CasinoPlayerGames, CasinoPlayerGamesWidgetModel> =
  useWidget;
